import { ChangeEvent } from "react";
import { NumericFormat } from "react-number-format";
import { FormikErrors } from "formik";
import { useQueryClient } from "@tanstack/react-query";
import { MEP_SENEBI_THRESHOLD } from "../../constants";

// TYPES
import { MepQuotes, OperateFormValues } from "../../types";

// UTILS
import { defaultNumericFormatProps } from "../../../../utils";
import {
  cleanInputString,
  formatCurrency,
  trunc,
} from "../../utils";

// UI
import { Card } from "@nextui-org/react";
import Skeleton from "../../../Skeleton/Skeleton";

// STYLES
import styles from "./AmountSelect.module.scss";

interface Props {
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean | undefined
  ) => Promise<void> | Promise<FormikErrors<OperateFormValues>>;
  isBuyingSide: boolean;
  operationValueArs: string;
  operationValueMep: string;
  isTotal: boolean;
  availableArs: string;
  availableMep: string;
  setValues: (
    values: React.SetStateAction<OperateFormValues>,
    shouldValidate?: boolean
  ) => Promise<FormikErrors<OperateFormValues>> | Promise<void>;
  symbolPositionLoading?: boolean;
}

const AmountSelect = ({
  setFieldValue,
  isBuyingSide,
  operationValueArs,
  operationValueMep,
  isTotal,
  availableArs,
  availableMep,
  setValues,
  symbolPositionLoading,
}: Props) => {
  const {
    container,
    itemWrapper,
    inputHeader,
    available,
    inputText,
    inputWrapper,
    itemNameContainer,
    amountValue,
    hasValue,
    noValue,
    investAllContainer,
    hasBorder,
    checkbox,
    currencyPrefix,
    prefixHasValue,
    prefixNoValue,
    disabledText,
  } = styles;

  const queryClient = useQueryClient();
  const mepQuotes = queryClient.getQueryData<MepQuotes>(["mepPrice"]);

  const isDisabled = isBuyingSide
    ? Number(availableArs) <= 0
    : Number(availableMep) <= 0;

  // -------- On value change -------- //

  const onValueChange = (currencyCode: "ARS" | "MEP", inputValue: string) => {
    if (!inputValue) {
      setValues(
        (prevValues) => ({
          ...prevValues,
          operationValueArs: "",
          operationValueMep: "",
          isTotal: false,
        }),
        true
      );
      return;
    }

    const field =
      currencyCode === "ARS" ? "operationValueArs" : "operationValueMep";
    const oppositeField =
      currencyCode === "ARS" ? "operationValueMep" : "operationValueArs";

    let floatValue = trunc(Number(cleanInputString(inputValue)));

    const pptPrice = isBuyingSide ? mepQuotes?.PPT?.BUY : mepQuotes?.PPT?.SELL;
    const senebiPrice = isBuyingSide ? mepQuotes?.SENEBI?.BUY : mepQuotes?.SENEBI?.SELL;
    const usdForThreshold = currencyCode === "MEP" ? floatValue : floatValue / (pptPrice || 1);
    const mepPriceValue = (usdForThreshold < MEP_SENEBI_THRESHOLD ? pptPrice : senebiPrice) ?? 0;

    let result = "";
    let isTotal = false;

    if (currencyCode === "ARS") {
      isTotal = floatValue === trunc(Number(availableArs));
      if (isTotal) floatValue = Number(availableArs);
      if (!isNaN(floatValue) && mepPriceValue > 0)
        result = (floatValue / mepPriceValue).toString();
    } else {
      isTotal = floatValue === trunc(Number(availableMep));
      if (isTotal) floatValue = Number(availableMep);
      if (!isNaN(floatValue)) result = (floatValue * mepPriceValue).toString();
    }

    setValues(
      (prevValues) => ({
        ...prevValues,
        [field]: floatValue.toString(),
        [oppositeField]: result,
        isTotal: isTotal,
      }),
      true
    );
  };

  // -------- Checkbox change -------- //

  const onCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFieldValue("isTotal", event.target.checked);

    if (event.target.checked) {
      if (isBuyingSide) {
        onValueChange("ARS", formatCurrency(Number(availableArs)));
      } else {
        onValueChange("MEP", formatCurrency(Number(availableMep)));
      }
    } else {
      setValues(
        (prevValues) => ({
          ...prevValues,
          operationValueArs: "",
          operationValueMep: "",
          isTotal: false,
        }),
        true
      );
    }
  };

  // -------- Amount change -------- //

  const currencyCodes: { c1: "ARS" | "MEP"; c2: "ARS" | "MEP" } = isBuyingSide
    ? { c1: "ARS", c2: "MEP" }
    : { c1: "MEP", c2: "ARS" };

  const getInput = (currencyCode: "ARS" | "MEP") => {
    const value =
      currencyCode === "ARS" ? operationValueArs : operationValueMep;
    return (
      <div className={amountValue}>
        <span
          className={`${currencyPrefix} ${
            value ? prefixHasValue : prefixNoValue
          }`}
        >
          <span>{currencyCode}</span>
          <span>{currencyCode === "ARS" ? "$" : "U$S"}</span>
        </span>

        <NumericFormat
          {...defaultNumericFormatProps}
          displayType="input"
          onChange={(e) => onValueChange(currencyCode, e.target.value)}
          value={value}
          placeholder="0,00"
          type="text"
          prefix=""
          className={`${inputText} ${inputWrapper} ${
            value ? hasValue : noValue
          }`}
        />
      </div>
    );
  };

  const getCheckboxText = () => {
    return isBuyingSide ? "Invertir todo" : "Vender todo";
  };

  return (
    <Card className={container} shadow="none">
      <div className={itemWrapper}>
        <div className={inputHeader}>
          Monto
          {symbolPositionLoading ? (
            <Skeleton width="30%" height="30px" />
          ) : (
            <div className={available}>
              <>
                <span>Disponible</span>{" "}
                <b>
                  <NumericFormat
                    {...defaultNumericFormatProps}
                    value={isBuyingSide ? availableArs : availableMep}
                    prefix={currencyCodes.c1 === "ARS" ? "$" : "U$S"}
                  />
                </b>
                <span> {currencyCodes.c1}</span>
              </>
            </div>
          )}
        </div>

        <div className={itemNameContainer}>
          {getInput(currencyCodes.c1)}
          <div
            className={`${investAllContainer} ${
              isDisabled ? disabledText : ""
            }`}
          >
            <span>{getCheckboxText()}</span>
            <input
              type="checkbox"
              className={checkbox}
              checked={isTotal}
              onChange={onCheckboxChange}
              disabled={isDisabled}
            />
          </div>
        </div>
        <div className={`${itemNameContainer} ${hasBorder}`}>
          {getInput(currencyCodes.c2)}
        </div>
      </div>
    </Card>
  );
};

export default AmountSelect;
